<template>
	<PageLoading :loading="loading || !user.uuid" class="relative">
		<PagePadding class="flex items-center justify-between">
			<ShieldIcon
				class="text-label-tertiary h-5 w-5"
				v-hover="{ active: 'scale-90', impact: 'soft' }"
				@click="openFairModal"
			/>
			<div
				class="font-squad text-label-primary text-center text-2xl leading-8 font-black uppercase"
			>
				{{ title }}
			</div>
			<ShareIcon
				class="text-label-tertiary h-5 w-5"
				v-hover="{ impact: 'soft' }"
				:class="{ 'pointer-events-none opacity-0': !price }"
				@click="share"
			/>
		</PagePadding>
		<PagePillRow class="pt-4">
			<BaseLabel class="relative snap-start">
				<div v-if="!funding">{{ t('cost') }}</div>
				<CoinPrice
					v-else
					size="xs"
					class="*:text-label-tertiary gap-0.5! line-through"
					:price="price"
					disabled
				/>
				<CoinPrice class="ml-1.5 gap-0.5!" size="xs" :price="fundedPrice" />
				<PriceDiscount :discount="funding" rotated />
			</BaseLabel>
			<BaseLabel
				class="snap-start text-[#B78EEF]!"
				v-if="modifiers.includes('crazy')"
			>
				<CrazyIcon class="mr-1.5 h-5 w-5" />
				<div>Crazy</div>
			</BaseLabel>
			<BaseLabel class="snap-start" v-if="mode">
				<div>{{ mode }}</div>
			</BaseLabel>
			<BaseLabel class="snap-start" v-if="status === 'running'">
				<div>{{ t('round') }}</div>
				<div class="text-label-primary ml-1">
					{{ currentRound + 1 }}/{{ cases.length }}
				</div>
			</BaseLabel>
			<BaseLabel class="snap-start" v-else>
				<div class="text-label-primary mr-1">
					{{ cases.length }}
				</div>
				<div>{{ t('rounds', cases.length) }}</div>
			</BaseLabel>
			<BaseLabel class="snap-start">
				{{ privateGame ? t('private') : t('public') }}
			</BaseLabel>
			<div class="pr-4" aria-hidden="true"></div>
		</PagePillRow>
		<RouletteBlock
			:items="currentItems"
			:count="participants.length"
			:price="currentPrice"
			:corners="status !== 'running'"
			:fast="modifiers.includes('fast')"
			:seed="rouletteSeed"
			noDrift
			class="mt-3 scroll-m-15"
			ref="rouletteBlock"
		>
			<template #header>
				<BattleSpin
					v-if="status === 'running'"
					ref="spinBlock"
					:participants="participants"
					:tallying="tallying"
					:isJackpot="gamemode === 'jackpot'"
				/>
				<BattleJackpotHeader
					v-if="gamemode === 'jackpot'"
					:show="status === 'running'"
					:rounds="roundsComputed"
					:participants="participants"
					:currentRound="
						status === 'running'
							? currentRound + (tallying ? 1 : 0)
							: cases.length
					"
					:isCrazy="modifiers.includes('crazy')"
				/>
			</template>
			<template #footer>
				<BattleCountdown
					v-if="tonBlockNumber"
					:show="status === 'waiting_for_block'"
					:timer="timer"
					:tonBlockNumber="tonBlockNumber"
				/>
				<BattleTieBreak
					v-if="status === 'running' || status === 'finished'"
					ref="tieBreak"
					:seed="serverSeedHash"
					:isJackpot="gamemode === 'jackpot'"
					:isCrazy="modifiers.includes('crazy')"
				/>
				<BattleEnd
					v-if="status === 'running' || status === 'finished'"
					:participants="participants"
					:wonCoins="wonCoins || 0"
					:price="price"
					:rounds="roundsComputed"
					:show="status === 'finished'"
					@watchAgain="watchAgain()"
					@recreate="recreate()"
				/>
				<BattleBottomStatus v-if="status !== 'running' || tallying">
					{{ statusText }}
				</BattleBottomStatus>
				<BattleBottomStatus v-else>
					<div v-if="currentCase">{{ currentCase.name }}</div>
					<div>-</div>
					<CoinPrice :price="currentPrice" />
				</BattleBottomStatus>
			</template>
			<template v-for="(player, i) in players" v-slot:[player.slotName]>
				<BattlePlayer
					:id="player.slotName"
					:index="i"
					:team="player.teamIndex"
					:avatar="player.avatar || undefined"
					:name="player.name || undefined"
					:action="actionType"
					:won="player.won"
					:eliminated="player.eliminated"
					:jackpot="jackpot"
					:isCrazy="modifiers.includes('crazy')"
					:participantTickets="participantTickets"
					@callBot="callBot(player.slotIndex, player.teamIndex)"
					@join="join(player.slotIndex, player.teamIndex)"
					:ref="
						(el) => (playerRefs[i] = el as InstanceType<typeof BattlePlayer>)
					"
				/>
			</template>
			<template
				#vs
				v-if="
					(status === 'created' || status === 'running') &&
					gamemode !== 'shared'
				"
			>
				<BattleVs
					class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
					:teamPlayers="teamPlayers"
				/>
			</template>
		</RouletteBlock>
		<BattleRounds
			class="mt-4"
			:cases="cases"
			:currentRound="currentRound"
			:active="status === 'running'"
		/>
		<PagePadding class="mt-5">
			<div class="space-y-3">
				<BattleTeamDrop
					v-for="(team, teamIndex) in teams"
					:teamIndex="teamIndex"
					:participants="team"
					:cases="cases"
					:casesData="casesData"
					:rounds="roundsComputed"
					:isJackpot="gamemode === 'jackpot'"
					:currentRound="
						status === 'running'
							? currentRound + (tallying ? 1 : 0)
							: cases.length
					"
					:isCrazy="modifiers.includes('crazy')"
				/>
			</div>
			<div
				class="text-label-tertiary mt-6 inline-flex items-center gap-1.5"
				@click="openFairModal"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
			>
				<ShieldIcon class="h-5 w-5" />
				<div class="text-sm font-medium capitalize">
					{{ t('fair') }}
				</div>
			</div>
		</PagePadding>
		<BattleFairModal
			:cases="fairCases"
			:rounds="fairRounds"
			:uuid="props.uuid"
			:tieBreakWinner="tieBreakWinner"
			:tieBreakCandidates="tieBreakCandidates"
			:gamemode="gamemode"
			:modifiers="modifiers"
			:teamIndexWinner="teamIndexWinner"
			:fair="fair"
			v-model="fairModal"
		/>
	</PageLoading>
</template>
<script setup lang="ts">
import ShareIcon from '@/assets/icons/share.svg'
import PagePadding from '@/components/PagePadding.vue'
import { useLayout } from '@/stores/layout'
import PagePillRow from '@/components/PagePillRow.vue'
import BaseLabel from '@/components/BaseLabel.vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { sleep } from '@/utils/sleep'
import { client } from '@/api/orpc'
import { animation, icon } from '@/utils/urls'
import RouletteBlock from '@/components/RouletteBlock.vue'
import BattlePlayer from '@/components/BattlePlayer.vue'
import { useUser } from '@/stores/user'
import BattleVs from '@/components/BattleVs.vue'
import BattleEnd from '@/components/BattleEnd.vue'
import BattleCountdown from '@/components/BattleCountdown.vue'
import BattleSpin from '@/components/BattleSpin.vue'
import BattleBottomStatus from '@/components/BattleBottomStatus.vue'
import CoinPrice from '@/components/CoinPrice.vue'
import BattleRounds from '@/components/BattleRounds.vue'
import CrazyIcon from '@/assets/icons/crazy.svg'
import { useBattles } from '@/stores/battles'
import { useRouter } from 'vue-router'
import PageLoading from '@/components/PageLoading.vue'
import BattleTeamDrop from '@/components/BattleTeamDrop.vue'
import ShieldIcon from '@/assets/icons/shield.svg'
import BattleFairModal from '@/components/modals/BattleFairModal.vue'
import BattleTieBreak from '@/components/BattleTieBreak.vue'
import { useI18n } from 'vue-i18n'
import PriceDiscount from '@/components/PriceDiscount.vue'
import { getItemColor } from '@/utils/itemColor'
import { useToast } from '@/stores/toast'
import { ORPCError } from '@orpc/client'
import { WebApp } from '@grammyjs/web-app'
import BattleJackpotHeader from '@/components/BattleJackpotHeader.vue'
import { useSeededRandom } from '@/composables/useSeededRandom'

const { t } = useI18n({
	messages: {
		en: {
			cost: 'cost',
			round: 'round',
			rounds: 'round | rounds',
			battle: 'battle',
			status: {
				waiting: 'waiting for players...',
				starting: 'starting soon...',
				showdown: 'showdown',
				tallying: 'tallying scores...'
			},
			unknownCase: 'Unknown Case',
			cta: `\n{0} Join my {1} battle on {'@'}ragegift ⤴️`,
			private: 'private',
			public: 'public',
			battleNotFound: 'Battle not found',
			fair: 'Provably Fair'
		},
		ru: {
			cost: 'цена',
			round: 'раунд',
			rounds: 'раундов | раунд | раунда | раундов',
			battle: 'батл',
			status: {
				waiting: 'ожидание игроков...',
				starting: 'скоро начало...',
				showdown: 'финал',
				tallying: 'подсчёт очков...'
			},
			unknownCase: 'Неизвестный кейс',
			cta: `\n{0} Заходи в мой {1} батл на {'@'}ragegift ⤴️`,
			private: 'приватный',
			public: 'публичный',
			battleNotFound: 'Батл не найден',
			fair: 'Честная Игра'
		}
	}
})

const props = defineProps<{
	uuid: string
}>()

const layout = useLayout()

layout.showBottomNavbar()

const currentRound = ref(0)

const serverSeed = ref<string>()
const serverSeedHash = ref<string>('')
const tonBlockAvgTime = ref<number>()
const tonBlockNumber = ref<number>()
const tonBlockHash = ref<string>()

const funding = ref<number>(0)
const startedAt = ref<number>()
const price = ref(0)
const wonCoins = ref<number>()
const cases = ref<string[]>([])
const casesData = ref<
	Record<
		string,
		{
			uuid: string
			name: string
			risk: number
			price: number
			items: {
				price: number
				modelUuid: string
				modelName: string
				collectionName: string
				preloader: string | null
				chance: number
				ticketsRange: [number, number]
			}[]
		}
	>
>({})
const participants = ref<
	{
		name: string | null
		avatar: string | null
		isBot: boolean | null
		teamIndex: number
		slotIndex: number
		winner: {
			wonCoins: number
		} | null
		me: boolean | null
	}[]
>([])
const rounds = ref<
	{
		uuid: string
		results: {
			participant: {
				teamIndex: number
				slotIndex: number
				name: string | null
			}
			item: {
				ticket: number
				index: number
			}
		}[]
		wonCoins: number
	}[]
>([])
const status = ref<'created' | 'waiting_for_block' | 'running' | 'finished'>()
const tallying = ref(false)
const finishedTallying = ref(false)
const privateGame = ref(false)
const modifiers = ref<('crazy' | 'fast')[]>([])
const gamemode = ref<'classic' | 'shared' | 'jackpot'>('classic')
const tieBreakWinner = ref<number | null>(null)
const tieBreakCandidates = ref<number[] | null>(null)
const teamIndexWinner = ref<number | null>(null)

const rouletteSeed = computed(() => {
	return `${serverSeedHash.value}:${currentRound.value}`
})

const fundedPrice = computed(() =>
	funding.value
		? Math.ceil((price.value * (100 - funding.value)) / 100)
		: price.value
)

const roundsComputed = computed(() =>
	rounds.value.map((round, i) => {
		const caseUuid = cases.value[i] || ''

		return {
			uuid: round.uuid,
			results: round.results.map((result) => {
				const item =
					casesData.value[caseUuid].items[result.item.index] || undefined

				return {
					participant: result.participant,
					item
				}
			})
		}
	})
)

const title = computed(() => {
	return `${participants.value
		.reduce((acc, participant) => {
			if (!acc[participant.teamIndex]) {
				acc[participant.teamIndex] = 0
			}
			acc[participant.teamIndex]++
			return acc
		}, [] as Array<number>)
		.join('v')} ${t('battle')}`
})

const statusText = computed(() => {
	if (status.value === 'created') {
		return t('status.waiting')
	}
	if (status.value === 'waiting_for_block') {
		return t('status.starting')
	}
	if (tieBreaking.value) {
		return t('status.showdown')
	}
	if (tallying.value) {
		return t('status.tallying')
	}

	return ''
})

const mode = computed(() => {
	const fast = modifiers.value.includes('fast') ? 'fast ' : ''

	if (gamemode.value === 'shared') {
		return `${fast}shared`
	} else if (gamemode.value === 'jackpot') {
		return `${fast}jackpot`
	}

	return `${fast}classic`
})

const modeColor = computed(() => {
	if (modifiers.value.includes('crazy')) {
		return '#B78EEF !important'
	}

	return
})

const tallyPlayers = ref<
	{
		slotIndex: number
		teamIndex: number
		won: boolean
		eliminated: boolean
	}[]
>([])

const players = computed(() =>
	status.value === 'created' || tallying.value || finishedTallying.value
		? participants.value.map((participant, i) => {
				const tallyPlayer = tallyPlayers.value.find(
					(p) =>
						p.slotIndex === participant.slotIndex &&
						p.teamIndex === participant.teamIndex
				)

				return {
					...participant,
					slotName: `player${i + 1}`,
					won: tallyPlayer?.won,
					eliminated: tallyPlayer?.eliminated
				}
			})
		: []
)

const participantTickets = computed(() => {
	const result: { teamIndex: number; tickets: number }[] =
		participants.value.map((_, i) => ({ teamIndex: i, tickets: 0 }))

	for (let i = 0; i < roundsComputed.value.length; i++) {
		const round = roundsComputed.value[i]

		for (let j = 0; j < round.results.length; j++) {
			const resultItem = round.results[j]

			result[j].tickets += resultItem.item?.price || 0
		}
	}

	return result
})

const playerRefs = ref<InstanceType<typeof BattlePlayer>[]>([])

const teamPlayers = computed(
	() => participants.value.reduce((a, b) => Math.max(a, b.slotIndex), 0) + 1
)

const teams = computed(() => {
	return participants.value.reduce(
		(acc, participant) => {
			if (!acc[participant.teamIndex]) {
				acc[participant.teamIndex] = []
			}
			acc[participant.teamIndex].push(participant)
			return acc
		},
		[] as Array<typeof participants.value>
	)
})

const isUserCreator = ref(false)
const isUserJoined = ref(false)

const actionType = computed(() => {
	if (isUserCreator.value && !isUserJoined.value) {
		return 'join_and_call_bot'
	}
	if (isUserCreator.value) {
		return 'call_bot'
	}
	if (isUserJoined.value) {
		return 'none'
	}

	return 'join'
})

const currentCase = computed(() => {
	const currentCaseUuid = cases.value[currentRound.value]

	if (!currentCaseUuid) {
		return
	}

	const caseItem = casesData.value[currentCaseUuid]

	if (!caseItem) {
		return
	}

	return caseItem
})

const currentItems = computed<[string, string | undefined, number][]>(() => {
	if (!currentCase.value || status.value === 'finished') {
		return []
	}

	return currentCase.value.items.map((item) => [
		icon(item.modelUuid),
		item.preloader || undefined,
		item.price
	])
})

const currentPrice = computed(() => {
	if (!currentCase.value) {
		return 0
	}

	return currentCase.value.price
})

const jackpot = computed(() => {
	if (gamemode.value !== 'jackpot') {
		return undefined
	}

	return wonCoins.value
})

const timer = ref(0)

const countdown = async () => {
	timer.value = tonBlockAvgTime.value || 5

	return new Promise<void>((res) => {
		let interval: number = 0

		interval = setInterval(() => {
			timer.value--

			if (!timer.value) {
				clearInterval(interval)
				res()
			}
		}, 1000)
	})
}

const rouletteBlock = ref<InstanceType<typeof RouletteBlock>>()

const spin = async (
	items: {
		src: string
		imgSrc: string
		preloader?: string | null
		price: number
		chance: number
	}[],
	pullbackCallback: (index: number) => void
) => {
	if (!items.length) {
		return
	}
	if (!rouletteBlock.value) {
		return
	}

	await rouletteBlock.value.spin(items, pullbackCallback)
}

const spinBlock = ref<InstanceType<typeof BattleSpin>>()

const shine = async (index: number) => {
	if (!spinBlock.value) {
		return
	}

	await spinBlock.value.shine(index)
}

const showItemInfo = async (
	index: number,
	item: {
		modelName: string
		collectionName: string
		color: 'orange' | 'pink' | 'red' | 'blue'
		price: number
	}
) => {
	if (!spinBlock.value) {
		return
	}

	await spinBlock.value.showItemInfo(index, item)
}

const hideItemInfo = async (index: number) => {
	if (!spinBlock.value) {
		return
	}

	await spinBlock.value.hideItemInfo(index)
}

const tieBreak = ref<InstanceType<typeof BattleTieBreak>>()
const tieBreaking = ref(false)

const rollTieBreak = async (
	avatars: [string, number, number][],
	avatar: [string, number]
) => {
	if (!tieBreak.value) {
		return
	}

	tieBreaking.value = true

	await tieBreak.value.roll(avatars, avatar)

	tieBreaking.value = false
}

const setTallyAmount = async (
	participantIndex: number,
	amount: number,
	animate?: boolean,
	duration?: number,
	blink?: boolean
) => {
	const playerRef = playerRefs.value[participantIndex]

	if (!playerRef) {
		return
	}

	return playerRef.setAmount(amount, animate, duration, blink)
}

const tally = async () => {
	tallying.value = true
	finishedTallying.value = false

	tallyPlayers.value = []

	const amounts = [0, 0, 0, 0]

	for (let i = 0; i < roundsComputed.value.length; i++) {
		const round = roundsComputed.value[i]

		if (!round) {
			continue
		}

		for (let j = 0; j < round.results.length; j++) {
			const result = round.results[j]

			if (!result || !result.item) {
				continue
			}

			amounts[j] += result.item.price
		}
	}

	await nextTick()

	if (!playerRefs.value.length) {
		for (let i = 0; i < 100; i++) {
			await nextTick()
			await sleep(10)

			if (playerRefs.value.length) {
				break
			}
		}
	}

	for (let i = 0; i < participants.value.length; i++) {
		setTallyAmount(i, 0)
	}

	// tally

	const promises = []

	const duration = modifiers.value.includes('fast') ? 1500 : 3000

	for (let i = 0; i < participants.value.length; i++) {
		const participant = participants.value[i]

		if (!participant) {
			continue
		}

		promises.push(setTallyAmount(i, amounts[i], true, duration, true))

		await sleep(300)
	}

	await Promise.all(promises)

	// tally end

	// regular tie-break
	if (tieBreakWinner.value !== null && tieBreakCandidates.value !== null) {
		await sleep(500)

		await rollTieBreak(
			tieBreakCandidates.value.map((index) => [
				participants.value[index].avatar || '',
				participants.value[index].teamIndex || 0,
				1
			]),
			[
				participants.value[tieBreakWinner.value || 0].avatar || '',
				participants.value[tieBreakWinner.value || 0].teamIndex || 0
			]
		)
	}

	// jackpot tie-break
	if (gamemode.value === 'jackpot') {
		const seededRandom = useSeededRandom(serverSeedHash.value)
		const winners = participants.value.filter((p) => p.winner?.wonCoins)
		const winner = winners[Math.floor(seededRandom.next() * winners.length)]

		await sleep(500)

		await rollTieBreak(
			participants.value.map((participant, i) => [
				participant.avatar || '',
				participant.teamIndex || 0,
				amounts[i]
			]),
			[winner.avatar || '', winner.teamIndex || 0]
		)
	}

	// winner tally

	if (gamemode.value !== 'jackpot') {
		await sleep(500)

		const promises2 = []

		for (let i = 0; i < participants.value.length; i++) {
			const participant = participants.value[i]

			if (!participant) {
				continue
			}

			if (participant.winner && participant.winner.wonCoins) {
				promises2.push(
					setTallyAmount(i, participant.winner.wonCoins, true, duration, true)
				)
			} else {
				promises2.push(setTallyAmount(i, 0, true, duration))
			}

			tallyPlayers.value.push({
				teamIndex: participant.teamIndex,
				slotIndex: participant.slotIndex,
				won: !!(participant.winner && participant.winner.wonCoins),
				eliminated: !!(participant.winner && !participant.winner.wonCoins)
			})
			await sleep(300)
		}

		await Promise.all(promises2)

		await sleep(1500)
	}

	// winner tally end

	tallying.value = false
	finishedTallying.value = true
}

const play = async (startFromIndex?: number) => {
	finishedTallying.value = false
	status.value = 'running'

	for (let i = startFromIndex || 0; i < roundsComputed.value.length; i++) {
		currentRound.value = i

		const round = roundsComputed.value[i]

		if (!round) {
			continue
		}

		await nextTick()

		const priceToCompare = modifiers.value.includes('crazy')
			? round.results.reduce(
					(a, result) => Math.min(a, result.item?.price || 0),
					0
				)
			: round.results.reduce(
					(a, result) => Math.max(a, result.item?.price || 0),
					0
				)

		const duplicatePrices =
			round.results.filter(
				(result) => (result.item?.price || 0) === priceToCompare
			).length > 1

		await spin(
			round.results.map((result) => ({
				src: animation(result.item?.modelUuid || ''),
				imgSrc: icon(result.item?.modelUuid || ''),
				preloader: result.item?.preloader,
				price: result.item?.price || 0,
				chance: result.item?.chance || 0
			})),
			async (index: number) => {
				const thisRound = round.results[index]

				if (!thisRound || !thisRound.item) {
					return
				}

				const color = getItemColor(thisRound.item, currentPrice.value)

				showItemInfo(index, {
					...thisRound.item,
					color
				})

				if (
					duplicatePrices ||
					!thisRound ||
					thisRound.item.price !== priceToCompare ||
					color === 'blue'
				) {
					return
				}

				shine(index)
			}
		)

		for (let j = 0; j < round.results.length; j++) {
			hideItemInfo(j)
		}
	}

	await tally()

	currentRound.value = 0
	status.value = 'finished'
}

const calcSettledAt = () => {
	if (!startedAt.value) {
		return 0
	}

	const fast = modifiers.value.includes('fast')
	let totalDuration = 0

	for (let i = 0; i < roundsComputed.value.length; i++) {
		const round = roundsComputed.value[i]
		const caseItem = cases.value[i]

		if (!round || !caseItem) {
			continue
		}

		const caseData = casesData.value[caseItem]
		const maxItemPrice = round.results.reduce(
			(a, result) => Math.max(a, result.item?.price || 0),
			0
		)

		if (caseData && maxItemPrice > caseData.price) {
			if (fast) {
				totalDuration += 1000
			} else {
				totalDuration += 3000
			}
		} else {
			totalDuration += 500
		}

		totalDuration += 700

		if (fast) {
			totalDuration += 1010 + participants.value.length * 100
		} else {
			totalDuration += 3010 + participants.value.length * 300
		}
	}

	totalDuration += fast ? 3000 : 6000 + participants.value.length * 600 + 2000

	return startedAt.value + totalDuration
}

const fastForward = async (
	eventStatus: 'finished' | 'created' | 'waiting_for_block' | 'running'
) => {
	if (!startedAt.value) {
		return
	}
	if (eventStatus !== 'finished') {
		return
	}

	const fast = modifiers.value.includes('fast')
	const diff = user.now() - startedAt.value
	let totalDuration = 0

	for (let i = 0; i < roundsComputed.value.length; i++) {
		const round = roundsComputed.value[i]
		const caseItem = cases.value[i]

		if (!round || !caseItem) {
			return
		}

		const caseData = casesData.value[caseItem]
		const maxItemPrice = round.results.reduce(
			(a, result) => Math.max(a, result.item?.price || 0),
			0
		)

		if (caseData && maxItemPrice > caseData.price) {
			if (fast) {
				totalDuration += 1000
			} else {
				totalDuration += 3000
			}
		} else {
			totalDuration += 500
		}

		totalDuration += 700

		if (fast) {
			totalDuration += 1010 + participants.value.length * 100
		} else {
			totalDuration += 3010 + participants.value.length * 300
		}

		if (totalDuration > diff) {
			return play(i)
		}
	}

	currentRound.value = rounds.value.length - 1
	status.value = 'running'

	totalDuration += (fast ? 3000 : 6000) + participants.value.length * 300

	if (tieBreakWinner.value !== null) {
		totalDuration += 3650
	}
	if (gamemode.value === 'jackpot') {
		totalDuration += 4650
	} else {
		totalDuration += participants.value.length * 300 + 2000
	}

	if (totalDuration > diff) {
		await tally()
	}

	currentRound.value = 0

	status.value = eventStatus
}

const calcSettlement = (addBalance?: boolean) => {
	const participant = participants.value.find((p) => p.me)

	if (!participant || !participant.winner || !participant.winner.wonCoins) {
		return
	}

	const res = user.resettleTransaction(props.uuid, calcSettledAt())

	if (res) {
		return
	}

	if (addBalance) {
		user.addBalance(participant.winner.wonCoins)
	}

	user.pushUnsettledTransactions([
		{
			uuid: props.uuid,
			coins: participant.winner.wonCoins,
			settledAt: calcSettledAt()
		}
	])
}

let iterator: Awaited<
	ReturnType<typeof client.battle.subBattleDetailedUpdates>
>['data']
const loading = ref(false)

const toast = useToast()

const createIterator = async () => {
	loading.value = true

	const { error, data, isDefined } =
		await client.battle.subBattleDetailedUpdates(
			{ uuid: props.uuid },
			{
				context: {
					retry: Number.POSITIVE_INFINITY,
					onRetry: () => {
						loading.value = true

						return (isSuccess) => {
							if (!isSuccess) {
								return
							}

							loading.value = false
						}
					}
				}
			}
		)

	if (isDefined) {
		if (error.code === 'BATTLE_NOT_FOUND') {
			toast.add({
				type: 'error',
				text: t('battleNotFound')
			})
			router.replace('/battles')
		}
		if (error.code === 'UNAUTHORIZED') {
			router.replace('/battles')
		}

		loading.value = false
		return
	}

	if (error) {
		router.replace('/battles')
		console.error(error)
		return
	}

	if (!data) {
		return
	}

	iterator = data

	try {
		for await (const event of iterator) {
			await user.nowAvailable()

			console.info('Battle event', event)

			funding.value = event.funding
			price.value = event.entryFee
			rounds.value = event.rounds
			privateGame.value = event.isPrivate
			modifiers.value = event.modifiers
			gamemode.value = event.gameMode
			cases.value = event.cases
			participants.value = event.participants
			casesData.value = event.casesData
			serverSeed.value = event.fair.serverSeed || undefined
			serverSeedHash.value = event.fair.serverSeedHash
			tonBlockAvgTime.value = event.fair.tonBlockAvgTime
			tonBlockNumber.value = event.fair.tonBlockNumber || undefined
			tonBlockHash.value = event.fair.tonBlockHash || undefined
			startedAt.value = event.ts ? event.ts : undefined
			wonCoins.value = event.wonCoins || undefined
			isUserCreator.value = event.isCreator
			isUserJoined.value = participants.value.some(
				(participants) => participants.me
			)
			tieBreakWinner.value = event.tieBreakWinner
			tieBreakCandidates.value = event.tieBreakCandidateIndices
			teamIndexWinner.value = event.teamIndexWinner

			if (event.status === 'running') {
				event.status = 'waiting_for_block'
			}

			if (event.status === 'waiting_for_block') {
				countdown()
			}

			if (!status.value || status.value === 'finished') {
				if (event.status === 'finished') {
					calcSettlement()
					fastForward(event.status)
				} else {
					status.value = event.status
				}
			} else {
				if (event.status === 'waiting_for_block') {
					status.value = event.status
				}

				if (event.status === 'finished') {
					calcSettlement(true)
					play()
				}
			}

			user.setDisplayCoins(true)
			loading.value = false
		}
	} catch (error: unknown) {
		console.error('ERROR IN BATTLES DETAILED SUB', error)

		if (error instanceof ORPCError && error.code === 'BATTLE_NOT_FOUND') {
			toast.add({
				type: 'error',
				text: t('battleNotFound')
			})
			router.replace('/battles')
		}
		if (error instanceof ORPCError && error.code === 'UNAUTHORIZED') {
			router.replace('/battles')
		}
	}
}

const user = useUser()

watch(
	() => user.token,
	() => {
		if (!user.token) {
			return
		}

		createIterator()
	},
	{ immediate: true }
)

onBeforeUnmount(() => {
	if (iterator && iterator.return) {
		iterator.return()
	}
})

const share = () => {
	const cta = t('cta', [
		modifiers.value.includes('crazy') ? '☠️' : '⚡️',
		mode.value.replace(' mode', '')
	])
	const link = `https://t.me/share/url?url=${encodeURIComponent(`https://t.me/ragegiftbot/app?startapp=battle-${props.uuid}`)}&text=${encodeURIComponent(cta)}`
	WebApp.openTelegramLink(link)
}

const callBot = async (slotIndex: number, teamIndex: number) => {
	if (status.value !== 'created') {
		return
	}
	if (!isUserCreator.value) {
		return
	}

	const { error, data, isDefined } = await client.battle.addBotToBattle({
		uuid: props.uuid,
		teamIndex,
		slotIndex
	})

	if (isDefined) {
		if (error.code === 'BATTLE_NOT_FOUND') {
			toast.add({
				type: 'error',
				text: t('battleNotFound')
			})
			router.replace('/battles')
		}

		return
	}

	if (error) {
		return console.error(error)
	}

	if (!data) {
		return
	}

	for (let i = 0; i < participants.value.length; i++) {
		const p = participants.value[i]

		if (
			p.teamIndex === data.participant.teamIndex &&
			p.slotIndex === data.participant.slotIndex
		) {
			participants.value[i] = { ...data.participant, winner: null }
			break
		}
	}
}

const join = async (slotIndex: number, teamIndex: number) => {
	if (status.value !== 'created') {
		return
	}
	if (fundedPrice.value > user.displayedCoins) {
		return layout.showDepositModal(Math.ceil(price.value - user.displayedCoins))
	}
	if (isUserJoined.value) {
		return
	}

	const { error, data, isDefined } = await client.battle.joinBattle({
		uuid: props.uuid,
		teamIndex,
		slotIndex
	})

	if (isDefined) {
		if (error.code === 'NOT_ENOUGH_COINS') {
			layout.showDepositModal(Math.ceil(price.value - user.displayedCoins))
		}
		if (error.code === 'BATTLE_NOT_FOUND') {
			toast.add({
				type: 'error',
				text: t('battleNotFound')
			})
			router.replace('/battles')
		}

		return
	}

	if (error) {
		return console.error(error)
	}

	if (!data) {
		return
	}

	for (let i = 0; i < participants.value.length; i++) {
		const p = participants.value[i]

		if (
			p.teamIndex === data.participant.teamIndex &&
			p.slotIndex === data.participant.slotIndex
		) {
			participants.value[i] = { ...data.participant, winner: null }
			break
		}
	}

	user.setUpdated(
		data.updatedUser.coins,
		data.updatedUser.rewards.level,
		data.updatedUser.rewards.xp,
		data.updatedUser.rewards.levelExpSize,
		data.updatedUser.rewards.levelPercent
	)
}

const watchAgain = async () => {
	status.value = 'waiting_for_block'

	await countdown()
	await play()
}

const battles = useBattles()
const router = useRouter()

const recreate = () => {
	const playersCount = participants.value
		.reduce((acc, participant) => {
			if (!acc[participant.teamIndex]) {
				acc[participant.teamIndex] = 0
			}
			acc[participant.teamIndex]++
			return acc
		}, [] as Array<number>)
		.join('v')

	let players = 0

	if (playersCount === '1v1') {
		players = 0
	}
	if (playersCount === '2v2') {
		players = 1
	}
	if (playersCount === '1v1v1') {
		players = 2
	}
	if (playersCount === '1v1v1v1') {
		players = 3
	}

	battles.setBattleParams(
		cases.value,
		gamemode.value,
		privateGame.value,
		modifiers.value,
		players
	)

	router.push('/battles/create')
}

const fairModal = ref(false)
const openFairModal = () => {
	fairModal.value = true
}

const fairCases = computed(() =>
	cases.value.map((caseUuid) => {
		const data = casesData.value[caseUuid]
		if (!data) {
			return {
				name: t('unknownCase'),
				items: []
			}
		}

		return {
			name: data.name,
			items: data.items.map((item) => ({
				modelUuid: item.modelUuid,
				ticketsRange: item.ticketsRange,
				chance: item.chance
			}))
		}
	})
)

const fairRounds = computed(() =>
	rounds.value.map((round, i) => {
		const caseUuid = cases.value[i] || ''

		return {
			results: round.results.map((result) => {
				const item = casesData.value[caseUuid].items[result.item.index] || {}

				return {
					participant: {
						slotIndex: result.participant.slotIndex,
						teamIndex: result.participant.teamIndex,
						name: result.participant.name
					},
					item: {
						...result.item,
						...item
					}
				}
			})
		}
	})
)

const fair = computed(() => ({
	serverSeed: serverSeed.value || null,
	serverSeedHash: serverSeedHash.value,
	tonBlockNumber: tonBlockNumber.value || null,
	tonBlockHash: tonBlockHash.value || null
}))
</script>
